import type { ProjectTask, TaskStatus } from "@/types/models";

export const DEFAULT_VISIBLE_STATUSES: readonly TaskStatus[] = [
  "Not started",
  "In progress",
  "Blocked",
  "In review",
  "Complete",
  "Cancelled",
];

export interface PlanTaskFilters {
  mine: boolean;
  organization: string;
  milestones: boolean;
  attention: boolean;
  hideInternal: boolean;
  statuses: TaskStatus[];
  showNotApplicable: boolean;
  query: string;
}

export function hasCustomStatusFilter(statuses: readonly TaskStatus[]) {
  return (
    statuses.length !== DEFAULT_VISIBLE_STATUSES.length ||
    DEFAULT_VISIBLE_STATUSES.some((status) => !statuses.includes(status))
  );
}

export function planTaskOrganizationOptions(
  tasks: readonly ProjectTask[],
  linkedOrganization?: string,
): string[] {
  const options = new Map<string, string>();
  for (const organization of [
    ...(linkedOrganization ? [linkedOrganization] : []),
    ...tasks.map((task) => task.ownerOrganization),
  ]) {
    const name = organization.trim();
    const key = name.toLocaleLowerCase();
    if (name && !options.has(key)) options.set(key, name);
  }
  return [...options.values()].sort((left, right) => left.localeCompare(right));
}

export function filterPlanTasks(
  tasks: readonly ProjectTask[],
  filters: PlanTaskFilters,
  currentUserId?: string,
  today = "",
  projectTargetEndDate = "",
): ProjectTask[] {
  const query = filters.query.trim().toLocaleLowerCase();
  return tasks.filter((task) => {
    if (filters.hideInternal && task.visibility === "Internal") return false;
    if (query && !task.title.toLocaleLowerCase().includes(query)) return false;
    if (filters.mine && task.ownerId !== currentUserId) return false;
    if (
      filters.organization !== "All organizations" &&
      task.ownerOrganization.toLocaleLowerCase() !==
        filters.organization.toLocaleLowerCase()
    )
      return false;
    if (filters.milestones && task.type !== "milestone") return false;
    if (
      filters.attention &&
      (task.type === "summary" ||
        ["Complete", "Not applicable", "Cancelled"].includes(task.status) ||
        !(
          task.status === "Blocked" ||
          (Boolean(today) && Boolean(task.dueDate) && task.dueDate < today) ||
          (Boolean(projectTargetEndDate) &&
            Boolean(task.dueDate) &&
            task.dueDate > projectTargetEndDate)
        ))
    )
      return false;
    if (task.status === "Not applicable") return filters.showNotApplicable;
    return filters.statuses.includes(task.status);
  });
}
